import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, ExternalLink, Users, Globe } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('cfe-krea/')) return `${API}/files/${logoUrl}`;
  return logoUrl;
};

const sectors = [
  'All Sectors',
  'Fintech',
  'EdTech',
  'HealthTech',
  'Sustainability',
  'AgriTech',
  'Craft & Creative',
  'SaaS',
  'E-Commerce',
  'Social Impact',
  'Other'
];

const stages = [
  'All Stages',
  'Idea',
  'Validation',
  'Prototype',
  'MVP',
  'Early Revenue',
  'Scaling'
];

const stageColors = {
  'Idea': 'bg-purple-100 text-purple-700',
  'Validation': 'bg-blue-100 text-blue-700',
  'Prototype': 'bg-cyan-100 text-cyan-700',
  'MVP': 'bg-green-100 text-green-700',
  'Early Revenue': 'bg-orange-100 text-orange-700',
  'Scaling': 'bg-red-100 text-red-700'
};

const helpOptions = [];

export default function StartupsPage() {
  const location = useLocation();
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSector, setSelectedSector] = useState('All Sectors');
  const [selectedStage, setSelectedStage] = useState('All Stages');
  const [selectedStartup, setSelectedStartup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchStartups = async () => {
      try {
        const response = await axios.get(`${API}/startups`);
        setStartups(response.data);
        if (location.state?.openStartupId) {
          const target = response.data.find(s => s.id === location.state.openStartupId);
          if (target) { setSelectedStartup(target); setIsModalOpen(true); }
        }
      } catch { /* silent */ } finally {
        setLoading(false);
      }
    };
    fetchStartups();
  }, []);

  const filteredStartups = startups.filter(startup => {
    const matchesSearch = startup.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         startup.tagline.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSector = selectedSector === 'All Sectors' || startup.sector === selectedSector;
    const matchesStage = selectedStage === 'All Stages' || startup.stage === selectedStage;
    return matchesSearch && matchesSector && matchesStage;
  });

  const handleViewDetails = (startup) => {
    setSelectedStartup(startup);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-[#F3F4F6]" data-testid="startups-hero">
        <div className="container-main">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[#C83041] font-semibold text-sm tracking-wider uppercase mb-4 block"
            >
              Portfolio
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-6xl font-bold text-[#231F20] mb-6 font-['Bricolage_Grotesque']"
              data-testid="startups-heading"
            >
              Startup Showcase
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-[#58595B] leading-relaxed"
            >
              Discover the innovative startups incubated and supported by the Centre for 
              Entrepreneurship at Krea University.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-white border-b border-gray-100 sticky top-20 z-30" data-testid="filters-section">
        <div className="container-main">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search startups..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 rounded-xl border-gray-200 focus:border-[#075FA3] focus:ring-[#075FA3]/20"
                data-testid="search-input"
              />
            </div>
            <Select value={selectedSector} onValueChange={setSelectedSector}>
              <SelectTrigger className="w-full md:w-48 py-6 rounded-xl" data-testid="sector-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sector" />
              </SelectTrigger>
              <SelectContent>
                {sectors.map(sector => (
                  <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedStage} onValueChange={setSelectedStage}>
              <SelectTrigger className="w-full md:w-48 py-6 rounded-xl" data-testid="stage-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Stage" />
              </SelectTrigger>
              <SelectContent>
                {stages.map(stage => (
                  <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Startups Grid */}
      <section className="section-padding bg-white" data-testid="startups-grid">
        <div className="container-main">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-2xl h-64"></div>
                </div>
              ))}
            </div>
          ) : filteredStartups.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <AnimatePresence>
                {filteredStartups.map((startup, index) => (
                  <motion.div
                    key={startup.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card 
                      className="h-full border border-gray-200 hover:border-[#075FA3]/50 hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
                      onClick={() => handleViewDetails(startup)}
                      data-testid={`startup-card-${startup.id}`}
                    >
                      <CardContent className="p-0">
                        {/* Logo/Header */}
                        <div className="h-40 bg-gradient-to-br from-[#075FA3]/10 to-[#4DBDDE]/10 flex items-center justify-center p-6">
                          {startup.logo_url ? (
                            <img 
                              src={getLogoUrl(startup.logo_url)} 
                              alt={startup.name}
                              className="max-h-full max-w-full object-contain"
                            />
                          ) : (
                            <div className="w-20 h-20 rounded-2xl bg-[#075FA3] flex items-center justify-center text-white text-3xl font-bold font-['Bricolage_Grotesque']">
                              {startup.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="p-6">
                          <div className="flex gap-2 mb-4">
                            <Badge className="badge-blue">{startup.sector}</Badge>
                            <Badge className={stageColors[startup.stage] || 'bg-gray-100 text-gray-700'}>
                              {startup.stage}
                            </Badge>
                          </div>
                          <h3 className="text-xl font-semibold text-[#231F20] mb-2 font-['Bricolage_Grotesque'] group-hover:text-[#075FA3] transition-colors">
                            {startup.name}
                          </h3>
                          <p className="text-[#58595B] text-sm line-clamp-2">
                            {startup.tagline}
                          </p>
                          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                            <span className="text-sm text-[#58595B]">
                              {startup.founders?.length > 0 ? `${startup.founders.length} Founder${startup.founders.length > 1 ? 's' : ''}` : 'Team'}
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-[#075FA3] hover:text-[#0077B6] p-0"
                            >
                              View Details
                              <ExternalLink className="ml-1 w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#F3F4F6] rounded-2xl p-12 text-center"
            >
              <div className="w-20 h-20 rounded-full bg-[#075FA3]/10 flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-[#075FA3]" />
              </div>
              <h3 className="text-xl font-semibold text-[#231F20] mb-3 font-['Bricolage_Grotesque']">
                {startups.length === 0 ? 'Startups Coming Soon' : 'No Startups Found'}
              </h3>
              <p className="text-[#58595B] max-w-md mx-auto">
                {startups.length === 0 
                  ? "We&apos;re currently building our portfolio of innovative startups. Check back soon to see the ventures we&apos;re supporting."
                  : "No startups match your current filters. Try adjusting your search criteria."
                }
              </p>
              {startups.length > 0 && (
                <Button 
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedSector('All Sectors');
                    setSelectedStage('All Stages');
                  }}
                  className="mt-6 btn-secondary"
                  data-testid="clear-filters-btn"
                >
                  Clear Filters
                </Button>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* Startup Detail Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedStartup && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4">
                  {selectedStartup.logo_url ? (
                    <img 
                      src={getLogoUrl(selectedStartup.logo_url)} 
                      alt={selectedStartup.name}
                      className="w-16 h-16 object-contain rounded-xl bg-gray-100 p-2"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-[#075FA3] flex items-center justify-center text-white text-2xl font-bold font-['Bricolage_Grotesque']">
                      {selectedStartup.name.charAt(0)}
                    </div>
                  )}
                  <div className="flex-1">
                    <DialogTitle className="text-2xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">
                      {selectedStartup.name}
                    </DialogTitle>
                    <p className="text-[#58595B] mt-1">{selectedStartup.tagline}</p>
                    <div className="flex gap-2 mt-3">
                      <Badge className="badge-blue">{selectedStartup.sector}</Badge>
                      <Badge className={stageColors[selectedStartup.stage] || 'bg-gray-100 text-gray-700'}>
                        {selectedStartup.stage}
                      </Badge>
                    </div>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-6">
                {selectedStartup.founders?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#231F20] mb-2 uppercase tracking-wide">Founders</h4>
                    <p className="text-[#58595B]">{selectedStartup.founders.join(', ')}</p>
                  </div>
                )}

                {selectedStartup.description && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#231F20] mb-2 uppercase tracking-wide">About</h4>
                    <p className="text-[#58595B] leading-relaxed">{selectedStartup.description}</p>
                  </div>
                )}

                {selectedStartup.problem && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#231F20] mb-2 uppercase tracking-wide">Problem</h4>
                    <p className="text-[#58595B] leading-relaxed">{selectedStartup.problem}</p>
                  </div>
                )}

                {selectedStartup.solution && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#231F20] mb-2 uppercase tracking-wide">Solution</h4>
                    <p className="text-[#58595B] leading-relaxed">{selectedStartup.solution}</p>
                  </div>
                )}

                {selectedStartup.milestones && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#231F20] mb-2 uppercase tracking-wide">Milestones</h4>
                    <p className="text-[#58595B] leading-relaxed">{selectedStartup.milestones}</p>
                  </div>
                )}

                {selectedStartup.help_needed?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#231F20] mb-2 uppercase tracking-wide">Support Provided</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedStartup.help_needed.map(help => (
                        <Badge key={help} variant="outline" className="border-[#C83041] text-[#C83041]">
                          {help}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {(selectedStartup.website_url || selectedStartup.contact_email) && (
                  <div className="flex gap-4 pt-4 border-t border-gray-100">
                    {selectedStartup.website_url && (
                      <a 
                        href={selectedStartup.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-[#075FA3] hover:text-[#0077B6] font-medium"
                      >
                        <Globe className="w-4 h-4" />
                        Website
                      </a>
                    )}
                    {selectedStartup.contact_email && (
                      <a 
                        href={`mailto:${selectedStartup.contact_email}`}
                        className="flex items-center gap-2 text-[#075FA3] hover:text-[#0077B6] font-medium"
                      >
                        Contact
                      </a>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
